import React from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  Descriptions,
  Table,
  Tag,
  Typography,
  Divider,
  Tabs,
  Badge,
  Space,
  Button,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  GlobalOutlined,
  BankOutlined,
  CalendarOutlined,
  DollarOutlined,
  PercentageOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  HRTaxLegislation,
  HRSocialContribution,
  HRTaxBracket,
  HRTaxAllowance,
  HRLegislationParameter,
} from "../../types/hr.d";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface TaxLegislationDetailsProps {
  open: boolean;
  onClose: () => void;
  legislation: HRTaxLegislation;
  onEdit?: () => void;
}

const TaxLegislationDetails: React.FC<TaxLegislationDetailsProps> = ({
  open,
  onClose,
  legislation,
  onEdit,
}) => {
  // Colonnes pour les cotisations sociales
  const socialContributionColumns: ColumnsType<HRSocialContribution> = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 100,
      render: (code) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Taux employé",
      dataIndex: "employee_rate",
      key: "employee_rate",
      width: 120,
      render: (rate) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <PercentageOutlined style={{ color: "#1890ff" }} />
          <span>{rate}%</span>
        </div>
      ),
    },
    {
      title: "Taux employeur",
      dataIndex: "employer_rate",
      key: "employer_rate",
      width: 120,
      render: (rate) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <PercentageOutlined style={{ color: "#52c41a" }} />
          <span>{rate}%</span>
        </div>
      ),
    },
    {
      title: "Base de calcul",
      dataIndex: "calculation_base",
      key: "calculation_base",
      width: 120,
      render: (base) => {
        const baseLabels = {
          gross_salary: "Salaire brut",
          net_salary: "Salaire net",
          taxable_salary: "Salaire imposable",
        };
        return (
          <Tag color="orange">
            {baseLabels[base as keyof typeof baseLabels]}
          </Tag>
        );
      },
    },
    {
      title: "Plafond",
      dataIndex: "max_base",
      key: "max_base",
      width: 120,
      render: (max) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <DollarOutlined style={{ color: "#faad14" }} />
          <span>
            {max
              ? `${max.toLocaleString()} ${legislation.currency_symbol}`
              : "Illimité"}
          </span>
        </div>
      ),
    },
    {
      title: "Statut",
      key: "status",
      width: 100,
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {record.is_active ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Actif
            </Tag>
          ) : (
            <Tag color="red" icon={<CloseCircleOutlined />}>
              Inactif
            </Tag>
          )}
          {record.is_mandatory && (
            <Tag color="gold" style={{ fontSize: "11px" }}>
              Obligatoire
            </Tag>
          )}
        </div>
      ),
    },
  ];

  // Colonnes pour les tranches d'imposition
  const taxBracketColumns: ColumnsType<HRTaxBracket> = [
    {
      title: "Tranche",
      dataIndex: "bracket_order",
      key: "bracket_order",
      width: 80,
      render: (order) => <Badge count={order} color="blue" />,
    },
    {
      title: "Montant minimum",
      dataIndex: "min_amount",
      key: "min_amount",
      width: 150,
      render: (amount) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <DollarOutlined style={{ color: "#52c41a" }} />
          <span>
            {amount.toLocaleString()} {legislation.currency_symbol}
          </span>
        </div>
      ),
    },
    {
      title: "Montant maximum",
      dataIndex: "max_amount",
      key: "max_amount",
      width: 150,
      render: (amount) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <DollarOutlined style={{ color: "#faad14" }} />
          <span>
            {amount
              ? `${amount.toLocaleString()} ${legislation.currency_symbol}`
              : "Illimité"}
          </span>
        </div>
      ),
    },
    {
      title: "Taux",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      render: (rate) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <PercentageOutlined style={{ color: "#1890ff" }} />
          <span>{rate}%</span>
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (desc) => desc || "-",
    },
  ];

  // Colonnes pour les abattements
  const taxAllowanceColumns: ColumnsType<HRTaxAllowance> = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 120,
      render: (code) => <Tag color="purple">{code}</Tag>,
    },
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => {
        const typeLabels = {
          personal: "Personnel",
          family: "Familial",
          professional: "Professionnel",
          other: "Autre",
        };
        const typeColors = {
          personal: "blue",
          family: "green",
          professional: "orange",
          other: "gray",
        };
        return (
          <Tag color={typeColors[type as keyof typeof typeColors]}>
            {typeLabels[type as keyof typeof typeLabels]}
          </Tag>
        );
      },
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: (amount, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <DollarOutlined style={{ color: "#52c41a" }} />
          <span>
            {amount.toLocaleString()}{" "}
            {record.is_percentage ? "%" : legislation.currency_symbol}
          </span>
        </div>
      ),
    },
    {
      title: "Max bénéficiaires",
      dataIndex: "max_beneficiaries",
      key: "max_beneficiaries",
      width: 120,
      render: (max) => max || "Illimité",
    },
    {
      title: "Conditions",
      dataIndex: "conditions",
      key: "conditions",
      render: (conditions) => (
        <Tooltip title={conditions || "Aucune condition"}>
          <Text ellipsis style={{ maxWidth: 200 }}>
            {conditions || "-"}
          </Text>
        </Tooltip>
      ),
    },
  ];

  // Colonnes pour les paramètres
  const parameterColumns: ColumnsType<HRLegislationParameter> = [
    {
      title: "Paramètre",
      dataIndex: "parameter_key",
      key: "parameter_key",
      width: 200,
      render: (key, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 500 }}>{key}</span>
          {record.is_system && (
            <Tag color="red" style={{ fontSize: "11px" }}>
              Système
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Valeur",
      dataIndex: "parameter_value",
      key: "parameter_value",
      width: 150,
      render: (value, record) => {
        if (record.parameter_type === "number") {
          return (
            <span style={{ fontFamily: "monospace" }}>
              {Number(value).toLocaleString()}
            </span>
          );
        } else if (record.parameter_type === "boolean") {
          return value === "true" ? (
            <Tag color="green">Oui</Tag>
          ) : (
            <Tag color="red">Non</Tag>
          );
        }
        return <span>{value}</span>;
      },
    },
    {
      title: "Type",
      dataIndex: "parameter_type",
      key: "parameter_type",
      width: 100,
      render: (type) => {
        const typeColors = {
          string: "blue",
          number: "green",
          boolean: "orange",
          json: "purple",
        };
        return (
          <Tag color={typeColors[type as keyof typeof typeColors]}>{type}</Tag>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (desc) => (
        <Tooltip title={desc || "Aucune description"}>
          <Text ellipsis style={{ maxWidth: 200 }}>
            {desc || "-"}
          </Text>
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <GlobalOutlined />
            <span>Législation fiscale - {legislation.country_name}</span>
          </div>
          {onEdit && (
            <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
              Modifier
            </Button>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={1400}
      style={{ top: 20 }}
    >
      <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
        {/* Informations générales */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Descriptions
                title="Informations générales"
                column={1}
                size="small"
              >
                <Descriptions.Item label="Pays">
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <GlobalOutlined style={{ color: "#1890ff" }} />
                    <span>
                      {legislation.country_name} ({legislation.country_code})
                    </span>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Devise">
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <DollarOutlined style={{ color: "#52c41a" }} />
                    <span>
                      {legislation.currency_symbol} ({legislation.currency_code}
                      )
                    </span>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Date effective">
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <CalendarOutlined style={{ color: "#faad14" }} />
                    <span>
                      {new Date(legislation.effective_date).toLocaleDateString(
                        "fr-FR"
                      )}
                    </span>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Statut">
                  <Space>
                    {legislation.is_active ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        Actif
                      </Tag>
                    ) : (
                      <Tag color="red" icon={<CloseCircleOutlined />}>
                        Inactif
                      </Tag>
                    )}
                    {legislation.is_default && (
                      <Tag color="gold">Par défaut</Tag>
                    )}
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions title="Statistiques" column={1} size="small">
                <Descriptions.Item label="Cotisations sociales">
                  <Badge
                    count={legislation.social_contributions_count || 0}
                    color="blue"
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Tranches d'imposition">
                  <Badge
                    count={legislation.tax_brackets_count || 0}
                    color="orange"
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Abattements">
                  <Badge
                    count={legislation.tax_allowances_count || 0}
                    color="green"
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Paramètres">
                  <Badge
                    count={legislation.parameters_count || 0}
                    color="purple"
                  />
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>

          {legislation.description && (
            <>
              <Divider />
              <div>
                <Title level={5}>Description</Title>
                <Text>{legislation.description}</Text>
              </div>
            </>
          )}
        </Card>

        {/* Détails par onglets */}
        <Tabs defaultActiveKey="social">
          {/* Cotisations sociales */}
          <TabPane
            tab={
              <span>
                <BankOutlined />
                Cotisations sociales (
                {legislation.social_contributions?.length || 0})
              </span>
            }
            key="social"
          >
            <Table
              columns={socialContributionColumns}
              dataSource={legislation.social_contributions || []}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1000 }}
              size="small"
            />
          </TabPane>

          {/* Tranches d'imposition */}
          <TabPane
            tab={
              <span>
                <PercentageOutlined />
                Tranches d'imposition ({legislation.tax_brackets?.length || 0})
              </span>
            }
            key="brackets"
          >
            <Table
              columns={taxBracketColumns}
              dataSource={legislation.tax_brackets || []}
              rowKey="id"
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />
          </TabPane>

          {/* Abattements */}
          <TabPane
            tab={
              <span>
                <DollarOutlined />
                Abattements ({legislation.tax_allowances?.length || 0})
              </span>
            }
            key="allowances"
          >
            <Table
              columns={taxAllowanceColumns}
              dataSource={legislation.tax_allowances || []}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1000 }}
              size="small"
            />
          </TabPane>

          {/* Paramètres */}
          <TabPane
            tab={
              <span>
                <InfoCircleOutlined />
                Paramètres ({legislation.parameters?.length || 0})
              </span>
            }
            key="parameters"
          >
            <Table
              columns={parameterColumns}
              dataSource={legislation.parameters || []}
              rowKey="id"
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );
};

export default TaxLegislationDetails;
